import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { services } from "@/lib/data/services";

const SERVICE_IMAGES = {
  "mining-security":
    "https://images.unsplash.com/photo-1655549136009-6aa6ff5de57e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxtaW5pbmclMjBpbmR1c3RyaWFsJTIwc2l0ZXxlbnwwfHx8fDE3ODUxMDU3MjB8MA&ixlib=rb-4.1.0&q=85",
  "mobile-patrol":
    "https://images.unsplash.com/photo-1680714246510-94d1055e4a8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85",
  "event-security":
    "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85",
  "corporate-security":
    "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85",
};

const DEFAULT_IMG = SERVICE_IMAGES["mobile-patrol"];

export default function ServiceRevealList() {
  const [hoverIdx, setHoverIdx] = useState(null);

  return (
    <section
      data-testid="service-reveal-list"
      className="relative bg-[#050505] py-24 md:py-40 overflow-hidden"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6 mb-16 md:mb-24">
        <div className="col-span-12 md:col-span-4">
          <p className="font-editorial italic text-[#FF3B30] text-sm">
            02 — Capabilities
          </p>
        </div>
        <div className="col-span-12 md:col-span-8">
          <h2 className="font-display font-black uppercase tracking-tightest leading-[0.9] text-white text-4xl md:text-6xl lg:text-7xl">
            Fifteen{" "}
            <span className="font-editorial italic font-normal text-[#a0a0a0]">
              disciplines,
            </span>
            <br />
            one operating standard.
          </h2>
        </div>
      </div>

      <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-10">
        {/* List column */}
        <div className="col-span-12 md:col-span-7 relative z-10">
          {services.map((s, i) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              data-testid={`service-row-${s.slug}`}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              className="group grid grid-cols-12 gap-4 items-baseline border-t border-[#1e1e1e] py-6 md:py-7 transition-colors duration-500 hover:border-[#FF3B30]"
            >
              <span className="col-span-2 font-editorial italic text-[#6a6a6a] text-lg group-hover:text-[#FF3B30] transition-colors">
                {s.number}
              </span>
              <span className="col-span-8 font-display font-black uppercase tracking-tight text-white text-2xl md:text-4xl lg:text-5xl leading-none group-hover:translate-x-2 transition-transform duration-500">
                {s.name}
              </span>
              <span className="col-span-2 flex justify-end">
                <ArrowUpRight
                  className="text-white/60 group-hover:text-[#FF3B30] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500"
                  size={20}
                />
              </span>
            </Link>
          ))}
          <div className="border-t border-[#1e1e1e]" />
        </div>

        {/* Fixed image reveal column */}
        <div className="hidden md:block col-span-5 relative">
          <div className="sticky top-32 aspect-[4/5] w-full overflow-hidden bg-[#0b0b0b] border border-[#1e1e1e]">
            <AnimatePresence mode="wait">
              <motion.img
                key={hoverIdx ?? "default"}
                src={SERVICE_IMAGES[services[hoverIdx ?? 0]?.slug] || DEFAULT_IMG}
                alt={services[hoverIdx ?? 0]?.name || "Security service"}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 h-full w-full object-cover grayscale contrast-125"
              />
            </AnimatePresence>
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#050505]/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <div>
                <p className="font-editorial italic text-[#FF3B30] text-sm">
                  {services[hoverIdx ?? 0]?.number}
                </p>
                <p className="font-display uppercase tracking-tight text-white text-xl">
                  {services[hoverIdx ?? 0]?.name}
                </p>
              </div>
              <span className="font-body text-xs uppercase tracking-[0.16em] text-white/70">
                {hoverIdx === null ? "Hover to preview" : "Live"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
